import type { FC } from 'react';
import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContactSupportBase from '@tmh-website/components/blocks/ContactSupport/ContactSupportBase';
import HorizontalScrollContainer from '@tmh-website/components/shared/HorizontalScrollContainer';
import Popup from '@tmh-website/ui/Popup';
import { Color } from '@tmh-website/shared/colors';
import { GlobalContext } from '@tmh-website/shared';
import { type LocalePrefix, LocaleToAudienceMap } from '@tmh-website/shared/const';

import type { HeaderThemeColor } from '../Header.theme';

import AudienceTab from '../AudienceTab/AudienceTab';
import * as Styled from './AudienceTabs.styles';

interface AudienceTabsProps {
  locale: LocalePrefix;
  headerThemeColor: HeaderThemeColor;

  /**
   * Enable this flag when this component is used outside of the NextJS context.
   */
  isStandaloneMode: boolean;

  inMenu?: boolean;
  showButton?: boolean;
  currentPath: string;
}

const AudienceTabs = ({
  locale,
  headerThemeColor,
  isStandaloneMode,
  showButton = true,
  currentPath,
  inMenu = false,
  ...props
}: AudienceTabsProps) => {
  const { audience, setAudience } = useContext(GlobalContext);
  const { t } = useTranslation();
  const [popupIsOpen, setPopupIsOpen] = useState(false);

  // isBreakpointLgUp is undefined on the initial render, so we have to check this like this to prevent layout shifts
  const ScrollContainer = inMenu ? HorizontalScrollContainer : Fragment;
  const scrollContainerProps = inMenu ? {} : { iconColor: Color.Neutral_0_White, fadeColor: Color.Blue_1000_Deep };

  return (
    <>
      <Styled.Container $inMenu={inMenu} $headerThemeColor={headerThemeColor} {...props}>
        <Styled.AdjustedWrapperLarge style={!inMenu ? { alignItems: 'flex-end' } : undefined}>
          <ScrollContainer {...(inMenu ? scrollContainerProps : {})}>
            <Styled.AudiencesContainer $inMenu={inMenu}>
              {LocaleToAudienceMap[locale].map(availableAudience => (
                <AudienceTab
                  key={availableAudience}
                  themeColor={headerThemeColor}
                  onClick={() => setAudience(availableAudience)}
                  isChecked={audience === availableAudience}
                  isStandaloneMode={isStandaloneMode}
                  targetAudience={availableAudience}
                  locale={locale}
                  inMenu={inMenu}>
                  {t(`global:audience.${availableAudience}`)}
                </AudienceTab>
              ))}
              {inMenu && (
                <AudienceTab
                  onClick={() => setPopupIsOpen(true)}
                  themeColor={Color.Blue_500_Ultramarine}
                  isStandaloneMode={isStandaloneMode}
                  locale={locale}
                  inMenu={inMenu}>
                  {t('global:contact.label')}
                </AudienceTab>
              )}
            </Styled.AudiencesContainer>
            {!inMenu && (
              <AudienceTab
                id='contact_pop_up'
                onClick={() => setPopupIsOpen(true)}
                themeColor={Color.Blue_500_Ultramarine}
                isStandaloneMode={isStandaloneMode}
                hasMargin={false}
                locale={locale}
                inMenu={inMenu}>
                {t('global:contact.label')}
              </AudienceTab>
            )}
          </ScrollContainer>
        </Styled.AdjustedWrapperLarge>
      </Styled.Container>
      <Popup
        layout='fullscreen-centered'
        themeColor={Color.Neutral_0_White}
        isOpen={popupIsOpen}
        onClose={() => setPopupIsOpen(false)}
        flexDirection='column'
        hasCloseButton
        isStandaloneMode={isStandaloneMode}
        maxWidth='1208px'>
        <ContactSupportBase
          themeColor={Color.Neutral_0_White}
          locale={locale as LocalePrefix}
          currentPath={currentPath}
        />
      </Popup>
    </>
  );
};

export default AudienceTabs;
